import { useRef } from "react";
import { Ellipsis, type LucideIcon } from "lucide-react";

import { Button } from "components/common/buttons/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/common/dropdown/DropDownV2";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/TooltipV3";

export interface HeaderMoreMenuItem {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
}

/**
 * Priority+ overflow for narrow headers: the actions that do not fit as
 * icon buttons stay one tap away in a "More" menu instead of disappearing.
 * Renders nothing when there is nothing to put in it.
 */
export const HeaderMoreMenu = ({ items }: { items: HeaderMoreMenuItem[] }) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  // An item was chosen: the dialog it opens takes focus, so the menu must not take it back.
  const itemSelectedRef = useRef(false);

  if (items.length === 0) return null;

  return (
    // Not modal, so a dialog opened from an item gets focus and pointer events.
    <DropdownMenu modal={false}>
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              ref={triggerRef}
              type="button"
              variant="ghost"
              size="sm"
              aria-label="More actions"
              className="inline-flex h-8 w-8 min-w-0 shrink-0 items-center justify-center rounded-md p-0 text-foreground hover:bg-muted"
            >
              <Ellipsis className="h-4 w-4" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" sideOffset={6}>
          More
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-52"
        onCloseAutoFocus={(event) => {
          // The shared wrapper never returns focus on close. Dismissing (Escape, a click
          // outside) puts it back on More; choosing an item leaves it where the item sent it.
          event.preventDefault();
          if (itemSelectedRef.current) {
            itemSelectedRef.current = false;
            return;
          }
          triggerRef.current?.focus();
        }}
      >
        {items.map((item) => (
          <DropdownMenuItem
            key={item.label}
            onSelect={() => {
              itemSelectedRef.current = true;
              item.onSelect();
            }}
          >
            <item.icon aria-hidden />
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
